import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TooltipPosition } from '@angular/material/tooltip';
import { RdsApiSnippetQueryType, SelectParameters, TabulationParameters } from '@rds/shared-api';

import {
  RdsApiSnippetDialogPositionX,
  RdsApiSnippetDialogPositionY,
  RdsApiSnippetsDialogComponent,
  RdsApiSnippetsDialogData,
} from './api-snippets-dialog.component';

/** Trigger button to open the RDS API Snippets Dialog. */
@Component({
  selector: 'rds-c-api-snippets-trigger',
  template: `
    <button
      #trigger
      type="button"
      mat-icon-button
      [attr.aria-label]="ariaLabel ?? tooltip"
      [disabled]="disabled || !params"
      [matTooltip]="tooltip"
      [matTooltipPosition]="tooltipPosition"
      (click)="showApiSnippetDialog()"
    >
      <mat-icon>{{ icon }}</mat-icon>
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RdsApiSnippetsTriggerComponent {
  static ngAcceptInputType_disabled: BooleanInput;
  static ngAcceptInputType_relativelyPositionDialog: BooleanInput;

  @ViewChild('trigger', { read: ElementRef, static: true }) triggerButton!: ElementRef<HTMLButtonElement>;

  /** Aria label for the snippets trigger. Defaults to 'RDS API usage and example code' */
  @Input() ariaLabel: string | null | undefined;
  /** Required. The type of query for which to generate snippets */
  @Input() queryType!: RdsApiSnippetQueryType;
  /** Required. The query parameters */
  @Input() params: SelectParameters | TabulationParameters | null | undefined;

  /** Snippet dialog height, defaults to 450px */
  @Input() dialogHeight = '450px';
  /** Horizontal position alignment for the snippet dialog, defaults to 'before' */
  @Input() dialogPositionX: RdsApiSnippetDialogPositionX = 'before';
  /** Vertical position alignment for the snippet dialog, defaults to 'below' */
  @Input() dialogPositionY: RdsApiSnippetDialogPositionY = 'below';
  /** Snippet dialog width, defaults to 600px */
  @Input() dialogWidth = '600px';
  /** Whether the trigger button is disabled */
  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }
  private _disabled = false;
  /** Button icon, defaults to 'code' */
  @Input() icon = 'code';
  /** Whether to relatively position the dialog to the trigger button, defaults to true */
  @Input()
  get relativelyPositionDialog() {
    return this._relativelyPositionDialog;
  }
  set relativelyPositionDialog(value: boolean) {
    this._relativelyPositionDialog = coerceBooleanProperty(value);
  }
  private _relativelyPositionDialog = true;
  /** Tooltip message, defaults to 'RDS API usage and example code' */
  @Input() tooltip = 'RDS API usage and example code';
  /** Tooltip position, defaults to 'below' */
  @Input() tooltipPosition: TooltipPosition = 'below';

  constructor(private dialog: MatDialog) {}

  showApiSnippetDialog() {
    if (this.params) {
      this.dialog.open(RdsApiSnippetsDialogComponent, {
        ariaLabel: 'RDS API usage and example code',
        height: this.dialogHeight,
        width: this.dialogWidth,
        data: new RdsApiSnippetsDialogData(
          this.queryType,
          this.params,
          this.relativelyPositionDialog ? this.triggerButton.nativeElement : undefined,
          this.dialogPositionX,
          this.dialogPositionY
        ),
      });
    }
  }
}
